/**
 * Physics Engine - Cursor Magnetism (REQ-PHYS-01, REQ-PHYS-02)
 * 
 * Since the webcam cursor is imprecise, this engine "sucks" the cursor onto buttons
 * using gravity well physics. This replaces expensive document.elementsFromPoint() calls.
 * 
 * @requirements 
 * - REQ-PHYS-01: React-aware spatial index for button positions
 * - REQ-PHYS-02: Gravity well logic with force vectors and stickiness
 */

interface Point {
  x: number;
  y: number;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MagnetTarget {
  id: string;
  element: HTMLElement;
  bounds: BoundingBox;
  center: Point;
  radius: number;
}

// Physics configuration
const PHYSICS_CONFIG = {
  // Gravity range in pixels - targets within this distance attract cursor
  GRAVITY_RANGE: 100,
  // Base gravity strength (higher = stronger pull)
  GRAVITY_STRENGTH: 0.15,
  // Friction multiplier when inside target (0-1, lower = slower)
  STICKINESS_FRICTION: 0.3,
  // Minimum distance to prevent division by zero
  MIN_DISTANCE: 1,
  // Maximum force cap to prevent jumping
  MAX_FORCE: 50,
};

/**
 * Simple spatial index using grid-based bucketing
 * More efficient than Quadtree for this use case (relatively few targets)
 */
class SpatialIndex {
  private targets: Map<string, MagnetTarget> = new Map();
  private cellSize: number = 100;
  private grid: Map<string, Set<string>> = new Map();

  /**
   * Get grid cell key for a position
   */
  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize);
    const cellY = Math.floor(y / this.cellSize);
    return `${cellX},${cellY}`;
  }

  /**
   * Get all cell keys that a bounding box overlaps
   */
  private getOverlappingCells(bounds: BoundingBox): string[] {
    const cells: string[] = [];
    const startX = Math.floor(bounds.x / this.cellSize);
    const endX = Math.floor((bounds.x + bounds.width) / this.cellSize);
    const startY = Math.floor(bounds.y / this.cellSize);
    const endY = Math.floor((bounds.y + bounds.height) / this.cellSize);

    for (let x = startX; x <= endX; x++) {
      for (let y = startY; y <= endY; y++) {
        cells.push(`${x},${y}`);
      }
    }
    return cells;
  }

  /**
   * Add a target to the index
   */
  add(target: MagnetTarget): void {
    this.targets.set(target.id, target);
    
    // Add to all overlapping grid cells
    const cells = this.getOverlappingCells(target.bounds);
    for (const cell of cells) {
      if (!this.grid.has(cell)) {
        this.grid.set(cell, new Set());
      }
      this.grid.get(cell)!.add(target.id);
    }
  }

  /**
   * Remove a target from the index
   */
  remove(id: string): void {
    const target = this.targets.get(id);
    if (!target) return;

    // Remove from grid cells
    const cells = this.getOverlappingCells(target.bounds);
    for (const cell of cells) {
      this.grid.get(cell)?.delete(id);
    }

    this.targets.delete(id);
  }

  /**
   * Update a target's bounds
   */
  update(id: string, bounds: BoundingBox): void {
    const target = this.targets.get(id);
    if (!target) return;

    // Remove from old cells
    const oldCells = this.getOverlappingCells(target.bounds);
    for (const cell of oldCells) {
      this.grid.get(cell)?.delete(id);
    }

    // Update bounds
    target.bounds = bounds;
    target.center = {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2,
    };
    target.radius = Math.max(bounds.width, bounds.height) / 2;

    // Add to new cells
    const newCells = this.getOverlappingCells(bounds);
    for (const cell of newCells) {
      if (!this.grid.has(cell)) {
        this.grid.set(cell, new Set());
      }
      this.grid.get(cell)!.add(id);
    }
  }

  /**
   * Query targets near a point
   */
  queryNear(point: Point, range: number): MagnetTarget[] {
    const results: MagnetTarget[] = [];
    const seen = new Set<string>();

    // Check cells in range
    const cellRange = Math.ceil(range / this.cellSize);
    const centerCellX = Math.floor(point.x / this.cellSize);
    const centerCellY = Math.floor(point.y / this.cellSize);

    for (let dx = -cellRange; dx <= cellRange; dx++) {
      for (let dy = -cellRange; dy <= cellRange; dy++) {
        const cellKey = `${centerCellX + dx},${centerCellY + dy}`;
        const cellTargets = this.grid.get(cellKey);
        
        if (cellTargets) {
          for (const id of cellTargets) {
            if (seen.has(id)) continue;
            seen.add(id);

            const target = this.targets.get(id);
            if (target) {
              // Check actual distance
              const distance = Math.sqrt(
                Math.pow(point.x - target.center.x, 2) +
                Math.pow(point.y - target.center.y, 2)
              );
              if (distance <= range + target.radius) {
                results.push(target);
              }
            }
          }
        }
      }
    }

    return results;
  }

  /**
   * Get target at exact point (for dwell detection)
   */
  getTargetAtPoint(point: Point): MagnetTarget | null {
    const cellKey = this.getCellKey(point.x, point.y);
    const cellTargets = this.grid.get(cellKey);

    if (cellTargets) {
      for (const id of cellTargets) {
        const target = this.targets.get(id);
        if (target) {
          const { bounds } = target;
          if (
            point.x >= bounds.x &&
            point.x <= bounds.x + bounds.width &&
            point.y >= bounds.y &&
            point.y <= bounds.y + bounds.height
          ) {
            return target;
          }
        }
      }
    }

    return null;
  }

  /**
   * Clear all targets
   */
  clear(): void {
    this.targets.clear();
    this.grid.clear();
  }

  /**
   * Get count of registered targets
   */
  get size(): number {
    return this.targets.size;
  }
}

/**
 * Physics Engine singleton
 */
class PhysicsEngineClass {
  private spatialIndex: SpatialIndex;
  private enabled: boolean = true;
  private magnetismStrength: 'none' | 'weak' | 'strong' = 'strong';

  constructor() {
    this.spatialIndex = new SpatialIndex();
  }

  /**
   * Register an interactive element
   */
  register(id: string, element: HTMLElement): void {
    const rect = element.getBoundingClientRect();
    const target: MagnetTarget = {
      id,
      element,
      bounds: {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      },
      center: {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      },
      radius: Math.max(rect.width, rect.height) / 2,
    };
    this.spatialIndex.add(target);
  }

  /**
   * Unregister an element
   */
  unregister(id: string): void {
    this.spatialIndex.remove(id);
  }

  /**
   * Update an element's position
   */
  updateBounds(id: string, element: HTMLElement): void {
    const rect = element.getBoundingClientRect();
    this.spatialIndex.update(id, {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }

  /**
   * Apply gravity force to cursor position
   * Returns adjusted position with magnetism applied
   * 
   * @requirements REQ-PHYS-02: Gravity well logic
   */
  applyGravity(rawX: number, rawY: number, velocityX: number = 0, velocityY: number = 0): {
    x: number;
    y: number;
    velocityX: number;
    velocityY: number;
    nearestTarget: MagnetTarget | null;
    isOverTarget: boolean;
  } {
    if (!this.enabled || this.magnetismStrength === 'none') {
      return {
        x: rawX,
        y: rawY,
        velocityX,
        velocityY,
        nearestTarget: null,
        isOverTarget: false,
      };
    }

    const strengthMultiplier = this.magnetismStrength === 'strong' ? 1.0 : 0.5;
    const range = PHYSICS_CONFIG.GRAVITY_RANGE * (this.magnetismStrength === 'strong' ? 1.0 : 0.7);

    // Query nearby targets
    const nearbyTargets = this.spatialIndex.queryNear({ x: rawX, y: rawY }, range);

    if (nearbyTargets.length === 0) {
      return {
        x: rawX,
        y: rawY,
        velocityX,
        velocityY,
        nearestTarget: null,
        isOverTarget: false,
      };
    }

    // Find nearest target and calculate gravity
    let nearestTarget: MagnetTarget | null = null;
    let nearestDistance = Infinity;
    let totalForceX = 0;
    let totalForceY = 0;
    let isOverTarget = false;

    for (const target of nearbyTargets) {
      const dx = target.center.x - rawX;
      const dy = target.center.y - rawY;
      const distance = Math.max(Math.sqrt(dx * dx + dy * dy), PHYSICS_CONFIG.MIN_DISTANCE);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestTarget = target;
      }

      // Check if cursor is inside target
      const isInside = 
        rawX >= target.bounds.x &&
        rawX <= target.bounds.x + target.bounds.width &&
        rawY >= target.bounds.y &&
        rawY <= target.bounds.y + target.bounds.height;

      if (isInside) {
        isOverTarget = true;
      }

      // Calculate gravity force (inverse distance)
      if (distance < range) {
        const force = (PHYSICS_CONFIG.GRAVITY_STRENGTH * strengthMultiplier) / 
                      Math.max(distance / range, 0.1);
        const cappedForce = Math.min(force, PHYSICS_CONFIG.MAX_FORCE);
        
        totalForceX += (dx / distance) * cappedForce;
        totalForceY += (dy / distance) * cappedForce;
      }
    }

    // Apply stickiness friction when over target
    let adjustedVelocityX = velocityX;
    let adjustedVelocityY = velocityY;
    
    if (isOverTarget) {
      adjustedVelocityX *= PHYSICS_CONFIG.STICKINESS_FRICTION;
      adjustedVelocityY *= PHYSICS_CONFIG.STICKINESS_FRICTION;
    }

    return {
      x: rawX + totalForceX,
      y: rawY + totalForceY,
      velocityX: adjustedVelocityX,
      velocityY: adjustedVelocityY,
      nearestTarget,
      isOverTarget,
    };
  }

  /**
   * Get target at point for dwell detection
   * Much faster than document.elementsFromPoint
   */
  getTargetAtPoint(x: number, y: number): MagnetTarget | null {
    return this.spatialIndex.getTargetAtPoint({ x, y });
  }

  /**
   * Set magnetism strength
   */
  setMagnetismStrength(strength: 'none' | 'weak' | 'strong'): void {
    this.magnetismStrength = strength;
  }

  /**
   * Enable/disable physics
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Clear all targets (for page transitions)
   */
  clear(): void {
    this.spatialIndex.clear();
  }

  /**
   * Get count of registered targets
   */
  get targetCount(): number {
    return this.spatialIndex.size;
  }
}

// Singleton instance
export const PhysicsEngine = new PhysicsEngineClass();

export type { MagnetTarget, Point, BoundingBox };
